import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '@/hooks';
import { DEFAULT_AUTHENTICATED_ROUTE, PUBLIC_ROUTES } from '@/constants/routes';

/**
 * OAuth Callback Handler
 * Handles Google OAuth login callbacks, extracts token and redirects
 * Note: User profile will be fetched by MainLayout when userName is empty
 */
export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokenAuth } = useAuth();

  useEffect(() => {
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');

    if (!accessToken) {
      toast.error('OAuth login failed: did not receive a valid token');
      navigate(PUBLIC_ROUTES.LOGIN, { replace: true });
      return;
    }

    // Save OAuth tokens - MainLayout will fetch user profile
    setTokenAuth({
      access_token: accessToken,
      refresh_token: refreshToken || '',
    });

    toast.success('OAuth login successful!');

    // Check for returnUrl from guest mode
    const returnUrl = sessionStorage.getItem('returnUrl');
    if (returnUrl) {
      sessionStorage.removeItem('returnUrl');
      navigate(returnUrl, { replace: true });
    } else {
      navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    }
  }, [searchParams, setTokenAuth, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      gap={2}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Processing login...
      </Typography>
    </Box>
  );
}
